import { SessionHistoryQueryRow } from "@p0security/shared/integrations/sessions/types";
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  ColumnType,
  Empty,
  Flex,
  Table,
  Typography,
} from "@p0security/ui";
import { nonNullable } from "@p0security/util/nonNullable";
import { FormattedDateTime } from "components/common/FormattedDateTime";
import stringify from "json-stable-stringify";
import React, { useCallback, useMemo, useState } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

import { getUserIdentity, parseUserAgent } from "../sessionUtils";
import { JsonViewerModal } from "./JsonViewerModal";

const formatUserAgent = (data?: string) => {
  if (!data) {
    return null;
  }
  const parsed = parseUserAgent(data);
  return parsed.type === "browser"
    ? parsed.type
    : parsed.type === "aws-cli"
      ? `aws-cli/${parsed.cliVersion} ${
          parsed.meta.cfg ? `| ${parsed.meta.cfg}` : ""
        } ${parsed.meta.md ? `| ${parsed.meta.md}` : ""}`
      : parsed.products.map((p) => p.name).join(", ");
};

export const QueryResultRowsTable: React.FC<{
  rows: SessionHistoryQueryRow[];
  update: React.ReactNode;
}> = ({ rows, update }) => {
  const [showErrors, setShowErrors] = useState(true);
  const [expandedJson, setExpandedJson] = useState<{
    data: unknown;
    title: string;
  } | null>(null);

  const handleExpandJson = useCallback((data: unknown, title: string) => {
    setExpandedJson({ data, title });
  }, []);

  const columns: ColumnType<SessionHistoryQueryRow>[] = [
    {
      title: "Raw Log",
      render: (data) => (
        <Button
          type="link"
          // antd will memoize this handler
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => handleExpandJson(data, "Raw Log")}
        >
          view
        </Button>
      ),
      minWidth: 30,
    },
    {
      title: "Event Time",
      dataIndex: "eventTime",
      key: "eventTime",
      render: (data?: string) => {
        if (!data) {
          return null;
        }
        // Add 'Z' to treat the string as UTC
        const localTimestamp = new Date(data + "Z").getTime();
        return <FormattedDateTime timestamp={localTimestamp} />;
      },
      minWidth: 150,
    },
    {
      title: "Action",
      dataIndex: "eventName",
      key: "eventName",
      render: (data?: string) => data,
    },
    {
      title: "Source",
      key: "eventSource",
      render: (data?: any) => {
        return (
          <>
            <Typography.Text>{data.eventSource}</Typography.Text>
            <div className="sub-text">{data.awsRegion}</div>
          </>
        );
      },
    },
    {
      title: "Identity",
      key: "userIdentity",
      render: (data?: any) => {
        const userIdentity = data.userIdentity;
        return (
          <>
            <Typography.Text>{getUserIdentity(userIdentity)}</Typography.Text>
            <div className="sub-text">{data.ipAddress}</div>
          </>
        );
      },
    },
    {
      title: "User Agent",
      dataIndex: "userAgent",
      key: "userAgent",
      render: (data?: string) => formatUserAgent(data),
    },
  ];

  if (showErrors) {
    columns.push({
      title: "Error",
      dataIndex: "errorCode",
      key: "errorCode",
      render: (data?: string) =>
        data ? (
          <StyledTag color={colors.tagColors.red}>{data}</StyledTag>
        ) : null,
    });
  }

  const stringifyRecord = useCallback(
    (record: SessionHistoryQueryRow) => nonNullable(stringify(record)),
    []
  );

  const toggleShowErrors = useCallback((e: CheckboxChangeEvent) => {
    setShowErrors(e.target.checked);
  }, []);

  const dataSource = useMemo(() => {
    return rows.filter((row) => {
      if (showErrors) {
        return true;
      }
      return !row.errorCode;
    });
  }, [rows, showErrors]);

  const handleCloseExpandedJson = useCallback(() => {
    setExpandedJson(null);
  }, []);

  return (
    <Flex vertical gap="middle">
      <Flex justify="space-between" align="center" className="w-full">
        {update}
        <Checkbox defaultChecked={showErrors} onChange={toggleShowErrors}>
          Show Errors
        </Checkbox>
      </Flex>
      <Table
        bordered
        columns={columns}
        dataSource={dataSource}
        rowKey={stringifyRecord}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: false,
          hideOnSinglePage: true,
        }}
        className="overflow-scroll"
        locale={{
          emptyText: (
            <Empty
              description="No activity"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
      <JsonViewerModal
        isOpen={!!expandedJson}
        onClose={handleCloseExpandedJson}
        data={expandedJson?.data}
        title={expandedJson?.title || ""}
      />
      <Typography.Text
        type="secondary"
        /* eslint-disable-line react/forbid-component-props */ style={{
          fontSize: "12px",
        }}
      >
        Note: The data shown here is based on the CloudTrail logs and may not
        reflect the most recent activity. It may take up to 5 minutes for the
        logs to be updated.
      </Typography.Text>
    </Flex>
  );
};
